import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ErrorState } from '@/shared/components/ErrorState';
import { formatDate, pluralise } from '@/shared/lib/format';
import type { DocumentDetail } from '../api/schemas';
import { PdfPane } from '../components/PdfPane';
import { ReportHeader } from '../components/ReportHeader';
import { ReportPanel } from '../components/ReportPanel';
import { panelTab } from '../components/ReportPanelTabs';
import { useDocumentQuery, useWhatChangedQuery } from '../hooks/useDocumentQuery';

/** One report: the PDF on the left, and beside it Ask (this report only), its summary, and what it revised. */
export function DocumentPage() {
  const { documentId = '' } = useParams();
  const { data: doc, isPending, isError, error, refetch } = useDocumentQuery(documentId);

  if (isPending) return <Skeleton variant="rectangular" sx={{ height: '100dvh' }} aria-label="Loading report" />;
  if (isError) return <Box sx={{ p: 4 }}><ErrorState error={error} onRetry={() => void refetch()} /></Box>;
  // Keyed by report, so moving to another report starts its viewer afresh.
  return <ReportView key={doc.id} doc={doc} />;
}

function ReportView({ doc }: { doc: DocumentDetail }) {
  const [params, setParams] = useSearchParams();
  const tab = panelTab(params.get('tab'));
  const page = Number(params.get('page')) || 1;
  const changes = useWhatChangedQuery(doc.id, true).data?.changes.length ?? 0;
  // The passage tinted in the PDF: the report's first call until another point is opened.
  const [opened, setOpened] = useState<{ text: string | null; page: number } | null>(null);
  const firstCall = doc.keyPoints[0];
  const tint = opened ?? { text: firstCall?.text ?? null, page: firstCall?.page ?? 1 };

  const set = (key: string, value: string) => {
    setParams((prev) => { const next = new URLSearchParams(prev); next.set(key, value); return next; }, { replace: true });
  };
  const open = (p: number, text: string | null = null) => { setOpened({ text, page: p }); set('page', String(p)); };
  const meta = `${doc.series} · ${formatDate(doc.publishDate)} · ${pluralise(doc.pages, 'page')}`;

  return (
    <Stack sx={{ height: { xs: 'auto', md: '100dvh' } }}>
      <ReportHeader doc={doc} meta={meta} />
      <Stack direction={{ xs: 'column', md: 'row' }} sx={{ flexGrow: 1, minHeight: 0 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0, height: { xs: '70vh', md: 'auto' } }}>
          <PdfPane url={doc.pdfUrl} title={doc.title} subtitle={meta} page={page} passage={tint.text} tintPage={tint.page} onPage={(p) => { set('page', String(p)); }} />
        </Box>
        <ReportPanel doc={doc} tab={tab} changes={changes} onTab={(t) => { set('tab', t); }} onOpen={open} />
      </Stack>
    </Stack>
  );
}
